<script setup lang="ts">
  import { PhoneNumbersService, type SuperUserPhoneNumber as PhoneNumber } from '@/types/generated'
  import PaginatedTableView, {
    type FetchDataOptions,
    type PaginatedResponse
  } from '@/components/admin/PaginatedTable.vue'
  import CustomerTableDataCell from '@/components/admin/table/CustomerTableDataCell.vue'
  import { ROUTES } from '@/router/routes'
  import UniqueTableDataCell from '@/components/admin/table/UniqueTableDataCell.vue'
  import { formatDateTimeGerman } from '../../../lib/util/formatting'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { TableRow, TableCell } from '@/components/ui/table'

  const LANGUAGE_STYLES: Record<string, { label: string; classes: string }> = {
    de: { label: 'DE', classes: 'bg-purple-50 text-purple-700 border border-purple-200' },
    en: { label: 'EN', classes: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    fr: { label: 'FR', classes: 'bg-violet-50 text-violet-700 border border-violet-200' },
    pl: { label: 'PL', classes: 'bg-blue-50 text-blue-700 border border-blue-200' }
  }

  const fetchPhoneNumbers = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<PhoneNumber>> => {
    return PhoneNumbersService.phoneNumbersList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search
    }) as unknown as PaginatedResponse<unknown>
  }
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Phone numbers', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2">
          <router-link
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            :to="ROUTES.ADMIN.VOICE.PHONE_NUMBER_CREATE"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Register phone number
          </router-link>
        </div>
      </div>
      <!-- End Page Header -->

      <!-- Users Table Card -->
      <div class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div>
          <!-- Tab Content -->
          <div class="">
            <!-- Table Section -->
            <div
              class="[&amp;::-webkit-scrollbar]:h-2 [&amp;::-webkit-scrollbar-thumb]:rounded-full
                [&amp;::-webkit-scrollbar-track]:bg-gray-100 [&amp;::-webkit-scrollbar-thumb]:bg-gray-300
                &amp;::-webkit-scrollbar-track]:bg-neutral-700 &amp;::-webkit-scrollbar-thumb]:bg-neutral-500
                overflow-x-auto"
            >
              <div class="inline-block min-w-full align-middle">
                <!-- Table -->
                <PaginatedTableView
                  :fetchData="fetchPhoneNumbers"
                  searchableFields="UUID, phone number or customer name"
                >
                  <template #table-head>
                    <TableCell>UUID</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>PhoneNumber</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell></TableCell>
                  </template>
                  <template #table-row="{ items: phoneNumbers }: { items: PhoneNumber[] }">
                    <TableRow
                      v-for="phoneNumber in phoneNumbers"
                      :key="phoneNumber.uuid"
                    >
                      <UniqueTableDataCell :truncate="true">
                        <router-link
                          :to="
                            ROUTES.ADMIN.VOICE.PHONE_NUMBER_DETAIL.replace(
                              ':uuid',
                              phoneNumber.uuid
                            )
                          "
                        >
                          {{ phoneNumber.uuid }}
                        </router-link>
                      </UniqueTableDataCell>
                      <TableCell>
                        {{ formatDateTimeGerman(phoneNumber.created_at) }}
                      </TableCell>
                      <TableCell>
                        {{ phoneNumber.active ? 'Yes' : 'No' }}
                      </TableCell>
                      <TableCell>
                        {{ phoneNumber.phone_number }}
                      </TableCell>
                      <TableCell>
                        <div
                          v-if="phoneNumber.agent"
                          class="flex items-center gap-2"
                        >
                          <span>
                            {{ phoneNumber.agent.agent_name || phoneNumber.agent.retell_agent_id }}
                          </span>
                          <span
                            v-if="
                              phoneNumber.agent.language &&
                              LANGUAGE_STYLES[phoneNumber.agent.language]
                            "
                            class="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none"
                            :class="LANGUAGE_STYLES[phoneNumber.agent.language]?.classes"
                          >
                            {{ LANGUAGE_STYLES[phoneNumber.agent.language]?.label }}
                          </span>
                        </div>
                        <span v-else>-</span>
                      </TableCell>
                      <CustomerTableDataCell :org="phoneNumber.org" />

                      <TableCell>
                        <router-link
                          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent text-sm font-semibold
                            text-blue-600 hover:text-blue-800 disabled:pointer-events-none disabled:opacity-50
                            dark:text-blue-500 dark:hover:text-blue-400"
                          :to="
                            ROUTES.ADMIN.VOICE.PHONE_NUMBER_DETAIL.replace(
                              ':uuid',
                              phoneNumber.uuid
                            )
                          "
                          type="button"
                        >
                          Details
                        </router-link>
                      </TableCell>
                    </TableRow>
                  </template>
                </PaginatedTableView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
